import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useApolloClient } from '@apollo/client';
import { useHasFeatureFlag } from '~/modules/common/hooks';
import { extractFirstValidationError } from '~/modules/common/graphql/errors';
import { useHasPermission } from '~/modules/common/permissions/useHasPermission';
import { addLocalRoleToCache } from '~/modules/common/hooks/useRoles';
import useAddRoleMutation from './useAddRoleMutation';

const mapValuesOnSubmit = ({
  name,
  skills,
  costRate,
  description,
  billingRate
}) => ({
  name,
  skills: skills ? mapSkillsValue(skills) : [],
  description,
  costRate: costRate ? mapMoneyValue(costRate) : null,
  billingRate: billingRate ? mapMoneyValue(billingRate) : null
});

export const mapSkillsValue = skills =>
  skills.map(skill => ({
    skillUri: skill.uri,
    skillLevelUri: skill.skillLevel ? skill.skillLevel.uri : null
  }));

export const mapMoneyValue = money =>
  money
    ? {
        amount: money.amount || 0,
        currency: money.currency
          ? {
              id: money.currency.id
            }
          : null
      }
    : null;

export const useAddRoleOnSubmit = ({
  onClose,
  onChange,
  includeSkills,
  refetchActiveRoles,
  onRoleCreated
}) => {
  const intl = useIntl();
  const isPsaPrpRoleFilterOnTasksEnabled = useHasFeatureFlag({
    featureFlag: 'isPsaPrpRoleFilterOnTasksEnabled'
  });
  const [addRole] = useAddRoleMutation();
  const hasViewPermission = useHasPermission({
    actionUri: 'urn:replicon:project-role-action:view-project-role'
  });

  const client = useApolloClient();

  const onSubmit = useCallback(
    async (values, { resetForm, setSubmitting, setFieldError, ...rest }) => {
      setSubmitting(true);

      const payload = mapValuesOnSubmit(values);
      const refetchQueries = [];

      if (refetchActiveRoles) {
        refetchQueries.push('activeRoles');
      }
      try {
        const { data } = await addRole({
          variables: {
            addRoleInput: payload,
            includeSkills,
            isCurrentRateEnabled: hasViewPermission
          },
          refetchQueries,
          awaitRefetchQueries: true
        });

        const { addRole2: role = {} } = data;

        addLocalRoleToCache({
          role,
          client,
          includeSkills,
          isCurrentRateEnabled: hasViewPermission
        });
        onChange(role);
        if (isPsaPrpRoleFilterOnTasksEnabled && onRoleCreated)
          onRoleCreated(role);

        setSubmitting(false);
        resetForm({});
        onClose();
      } catch (err) {
        setSubmitting(false);
        const validationErrors = extractFirstValidationError(err);

        if (validationErrors) {
          const duplicationNameError = validationErrors.find(
            v =>
              v.failureUri ===
              'urn:replicon:validation-failure:role-name-duplicated'
          );

          setFieldError(
            'name',
            duplicationNameError
              ? intl.formatMessage({
                  id: 'addRoleDialog.duplicateRole'
                })
              : null
          );
        }
      }
    },
    [
      refetchActiveRoles,
      hasViewPermission,
      includeSkills,
      onChange,
      onClose,
      addRole,
      client,
      intl
    ]
  );

  return { onSubmit };
};

export default useAddRoleOnSubmit;
